import React, { useState, useRef, useEffect } from 'react';
import { Mail } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { mobilePageBackground } from '../../../services/mobileStyleService';
import { getBaseballBackground } from './baseballAssets';

const MobileVerify = ({ onVerify, onResend, email, loading, error, message }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const digits = pastedData.match(/\d/g) || [];
    
    const newCode = [...code];
    digits.forEach((digit, i) => {
      if (i < 6) {
        newCode[i] = digit;
      }
    });
    setCode(newCode);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex(c => c === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const verificationCode = code.join('');
    if (verificationCode.length === 6) {
      onVerify(email, verificationCode);
    }
  };

  const isComplete = code.every(digit => digit !== '');

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ background: mobilePageBackground, ...getBaseballBackground() }}
    >
      {/* Header */}
      <div className="flex-shrink-0 text-center pt-16 pb-8 px-6">
        {/* Large email icon with baseball theme */}
        <div className="relative inline-block mb-6">
          <div className="text-6xl">📧</div>
          <div className="absolute -top-2 -right-2 text-3xl animate-bounce">⚾</div>
        </div>

        {/* Title */}
        <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white} mb-3`}>
          You're Almost In the Game!
        </h2>

        {/* Description */}
        <p className={`text-base ${dynastyTheme.classes.text.neutralLighter} mb-2`}>
          We sent a 6-digit code to:
        </p>
        <p className={`text-base ${dynastyTheme.classes.text.primary} font-semibold`}>
          {email}
        </p>
      </div>

      {/* Form */}
      <div className="flex-1 px-6 pb-8">
        <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto">
          {/* Success Message */}
          {message && (
            <div className={`${dynastyTheme.components.badge.success} p-4 rounded-lg text-center`}>
              {message}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className={`${dynastyTheme.components.badge.error} p-4 rounded-lg text-center`}>
              {error}
            </div>
          )}

          {/* Label */}
          <label className={`${dynastyTheme.components.label} text-center block`}>
            Enter your 6-digit code:
          </label>

          {/* 6-Box Input */}
          <div className="flex gap-2 justify-center" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-12 h-14 text-center text-2xl font-bold rounded-md border-2 ${dynastyTheme.classes.bg.neutral} ${dynastyTheme.classes.text.white} ${dynastyTheme.classes.transition}
                  ${digit ? `${dynastyTheme.classes.border.primary}` : `${dynastyTheme.classes.border.neutralLight}`}
                  focus:${dynastyTheme.classes.border.primary} focus:ring-2 focus:ring-yellow-400/20
                `}
              />
            ))}
          </div>

          {/* Verify Button */}
          <button
            type="submit"
            disabled={loading || !isComplete}
            className={`${dynastyTheme.utils.getComponent('button', 'primary', 'lg')} w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? (
              <>Verifying...</>
            ) : (
              <>
                <span>🏟️</span>
                Enter the Ballpark
              </>
            )}
          </button>

          {/* Resend Link */}
          <div className="text-center space-y-2">
            <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>
              Didn't get the pitch?
            </p>
            <button
              type="button"
              onClick={onResend}
              disabled={loading}
              className={`text-sm ${dynastyTheme.classes.text.primary} hover:${dynastyTheme.classes.text.primaryHover} ${dynastyTheme.classes.transition} font-semibold`}
            >
              Throw it again (resend code)
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MobileVerify;
